/**
 * `/` — Home. Liga `GET /v1/home` a tela.
 *
 * `staleTime: 0` e `gcTime: 0` de proposito: `API §8.1` marca a Home **autenticada** como
 * `private, no-store` (ela carrega `buy_again`, que e historico de compra, e `campaign`, que
 * carrega preco). Manter a resposta em memoria depois que ninguem observa e exatamente o que
 * `PWA §4.2 R11` proibe — num aparelho compartilhado em familia, a Home do titular anterior
 * apareceria para o proximo.
 */
import { useApiClient } from '@farmacia/api-client/query';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { buscarHome, chaveDaHome } from '../app/home.js';
import { useAdicionarAoCarrinho } from '../carrinho/useAdicionarAoCarrinho.js';
import { useMinhaLoja } from '../loja/contexto.jsx';
import { usePwa } from '../pwa/contexto.jsx';
import { Home } from '../telas/Home.jsx';

export const Route = createFileRoute('/')({ component: TelaInicial });

function TelaInicial(): ReactNode {
  const cliente = useApiClient();
  const { storeId, hidratando, abrirSeletor } = useMinhaLoja();
  const { conexao } = usePwa();
  const carrinho = useAdicionarAoCarrinho();

  const consulta = useQuery({
    queryKey: chaveDaHome(storeId ?? 'sem-filial'),
    // Sem filial o `queryFn` **rejeita** em vez de mandar `storeId=` vazio: a rota exige
    // `storeId` e um `422` renderizado como tela vazia esconde o defeito (ver `app/erros.ts`).
    queryFn: ({ signal }) =>
      storeId === null
        ? Promise.reject(new Error('Home sem filial escolhida: a consulta nao deveria sair.'))
        : buscarHome(cliente, { storeId }, signal),
    enabled: storeId !== null,
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <Home
      dados={consulta.data}
      carregando={consulta.isPending && storeId !== null}
      erro={consulta.error}
      aoTentarDeNovo={() => void consulta.refetch()}
      modoEconomia={conexao === 'DEGRADED'}
      semLoja={storeId === null && !hidratando}
      aoEscolherLoja={abrirSeletor}
      aoAdicionar={carrinho.adicionar}
      adicionandoId={carrinho.produtoEmVoo}
      erroAoAdicionar={carrinho.erroDe}
    />
  );
}
